const toSafeWorksheetName = (name) => {
  const s = String(name || '').trim() || 'Sheet1';
  // Excel worksheet name rules: max 31 chars, no: : \ / ? * [ ]
  return (
    s
      .replace(/[:\\/?*]/g, ' ')
      .replace(/\[/g, ' ')
      .replace(/]/g, ' ')
      .slice(0, 31) || 'Sheet1'
  );
};

const downloadBlob = (blob, filename) => {
  const safeName = String(filename || 'export.xlsx').trim() || 'export.xlsx';
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Give the browser a tick so the download can start.
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }
};

/**
 * Build and download an .xlsx file.
 *
 * sheets: [{
 *   name: string,
 *   columns: [{ header: string, key: string, width?: number, numFmt?: string }],
 *   rows: Array<object>
 * }]
 */
export async function exportSheetsToXlsx({ fileName, sheets }) {
  const list = Array.isArray(sheets) ? sheets : [];
  if (list.length === 0) return;

  // Use the browser build of exceljs to avoid Node-only deps.
  const mod = await import('exceljs/dist/exceljs.min.js');
  const ExcelJS = mod?.default || mod;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'ProPlanConnect';
  workbook.created = new Date();

  for (const sheet of list) {
    const ws = workbook.addWorksheet(toSafeWorksheetName(sheet?.name));

    const columns = Array.isArray(sheet?.columns) ? sheet.columns : [];
    ws.columns = columns.map((c) => ({
      header: String(c?.header ?? ''),
      key: String(c?.key ?? ''),
      width: Number.isFinite(Number(c?.width)) ? Number(c.width) : undefined,
      style: c?.numFmt ? { numFmt: String(c.numFmt) } : undefined,
    }));

    const rows = Array.isArray(sheet?.rows) ? sheet.rows : [];
    ws.addRows(rows);

    // Header styling.
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

    // Freeze header.
    ws.views = [{ state: 'frozen', ySplit: 1 }];

    // Improve readability.
    ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      row.alignment = rowNumber === 1
        ? { vertical: 'middle', horizontal: 'center', wrapText: true }
        : { vertical: 'top', horizontal: 'left', wrapText: true };
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  downloadBlob(blob, fileName || 'export.xlsx');
}
